import React, { FC, useState } from 'react';
import { useDataProvider } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from '../../../../../common/components/ParamsProvider';
import PruTable from '../../../../../common/components/PruTable/PruTable';
import PruFilter, { PruFilterItemType } from '../../../../../common/components/PruTable/PruFilter';
import { PaginateListMore } from 'src/app/common/types/common-types';
import { IReportFilter, YesAndNo, ITeamSalePlanningItem } from '../../../types/sale-planning-types';
import {
  fetchTeamSalePlanningReport,
  fetchTeamSalePlanningReportFile,
  fetchTotalRecordTeamSalePlanningReport,
} from '../../../network/salePlanningCrud';
import moment from 'moment';
import { numberSeparator } from '../../../../../common/utils/common-utils';

type TeamSalePlanningReportProps = {} & ParamsProps;

const initialState: IReportFilter = {
  planDate: moment().startOf('month').toDate(),
  agentCode: '',
  unitCode: '',
  officeCode: '',
  approval: YesAndNo.YES,
  status: YesAndNo.YES,
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): IReportFilter => {
  return initialParams
    ? {
        planDate: initialParams.planDate
          ? moment(initialParams.planDate).startOf('day').toDate()
          : moment().startOf('month').toDate(),
        agentCode: '',
        unitCode: '',
        officeCode: '',
        approval: YesAndNo.YES,
        status: YesAndNo.YES,
        page: 1,
        limit: 5,
      }
    : initialState;
};

const YesNoOptions = [
  { displayName: 'Yes', value: YesAndNo.YES },
  { displayName: 'No', value: YesAndNo.NO },
];

const TeamSalePlanningReport: FC<TeamSalePlanningReportProps> = ({ initialParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const defaultFilter = paramsInitiator(initialParams);

  const [filterState, setFilterState] = useState<IReportFilter>(defaultFilter);
  const [reportList, setReportList] = useState<PaginateListMore<ITeamSalePlanningItem>>();
  const { isLoading, refreshData } = useDataProvider<PaginateListMore<ITeamSalePlanningItem>>(
    async () => {
      try {
        const params = handleParams(filterState);
        const result = await Promise.all([
          fetchTeamSalePlanningReport(params, dispatch),
          fetchTotalRecordTeamSalePlanningReport(params, dispatch),
        ]);
        return {
          ...result[0],
          ...result[1],
        };
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsv = () => {
    try {
      const params = handleParams(filterState);
      fetchTeamSalePlanningReportFile(params, dispatch);
    } catch (err) {}
  };

  const handleParams = (filters: IReportFilter) => {
    return {
      ...filterState,
      planDate: moment(filterState.planDate).format('MM/YYYY'),
    };
  };

  return (
    <>
      <PruFilter
        title={Translation('report.salePlanning.teamSalePlanning.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('report.salePlanning.teamSalePlanning.filter.leaderCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'unitCode',
            initialValue: filterState.unitCode,
            displayName: Translation('report.salePlanning.teamSalePlanning.filter.unitCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'officeCode',
            initialValue: filterState.officeCode,
            displayName: Translation('report.salePlanning.teamSalePlanning.filter.officeCode'),
          },
          {
            type: PruFilterItemType.DATE_MONTH,
            field: 'planDate',
            initialValue: filterState.planDate,
            defaultValue: defaultFilter.planDate,
            displayName: Translation('report.salePlanning.teamSalePlanning.filter.planningMonth'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            initialValue: filterState.status,
            defaultValue: defaultFilter.status,
            displayName: Translation('report.salePlanning.teamSalePlanning.filter.salesPlanningStatus'),
            choices: YesNoOptions,
            disabled: true,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'approval',
            initialValue: filterState.approval,
            defaultValue: defaultFilter.approval,
            displayName: Translation('report.salePlanning.teamSalePlanning.filter.BDMApproval'),
            choices: YesNoOptions,
            disabled: filterState.status === YesAndNo.NO,
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            ...data,
          })
        }
        fetchData={refreshData}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.salePlanning.teamSalePlanning.title')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsv(),
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            keyIndex: 'ALRole',
            displayName: Translation('report.salePlanning.teamSalePlanning.ALRole'),
            align: 'center',
            renderData: (row) => row.leaderRole || '-',
          },
          {
            keyIndex: 'ALCode',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.ALCode'),
            renderData: (row) => row.leaderCode || '-',
          },
          {
            keyIndex: 'ALName',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.ALName'),
            renderData: (row) => row.leaderName || '-',
          },
          {
            keyIndex: 'unitCode',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.unitCode'),
            renderData: (row) => row.unitCode + (row.unitPUMCode ? `/${row.unitPUMCode}` : ''),
          },
          {
            keyIndex: 'officeCode',
            displayName: Translation('report.salePlanning.teamSalePlanning.officeCode'),
            align: 'center',
            renderData: (row) => row.officeCode || '-',
          },
          {
            keyIndex: 'salesPlanning',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.salesPlanning'),
            renderData: (row) => (row.salesPlanning ? 'Yes' : 'No'),
          },
          {
            keyIndex: 'planningMonth',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.planningMonth'),
            renderData: (row) => row.planDate || '-',
          },
          {
            keyIndex: 'activeAG',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.activeAG'),
            renderData: (row) => (row.activeAgent || 0).toString(),
          },
          {
            keyIndex: 'potentialCustomer',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.potentialCustomer'),
            renderData: (row) => (row.potentialCustomer || 0).toString(),
          },
          {
            keyIndex: 'customerAppointment',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.customerAppointment'),
            renderData: (row) => (row.customerAppointment || 0).toString(),
          },
          {
            keyIndex: 'productPresentation',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.productPresentation'),
            renderData: (row) => numberSeparator((row.productPresentation || 0).toString()),
          },
          {
            keyIndex: 'case',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.case'),
            renderData: (row) => (row.case || 0).toString(),
          },
          {
            keyIndex: 'FYP',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.FYP'),
            renderData: (row) => numberSeparator((row.FYP || 0).toString()),
          },
          {
            keyIndex: 'BDMApproval',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.BDMApproval'),
            renderData: (row) => row.BDMApproval,
          },
          {
            keyIndex: 'BDMName',
            align: 'center',
            displayName: Translation('report.salePlanning.teamSalePlanning.BDMName'),
            renderData: (row) => row.BDMName,
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default TeamSalePlanningReport;
